import React, { useRef, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useRole } from "@/hooks/useRole";
import { 
  Calendar, 
  Users, 
  Clock, 
  ShoppingCart,
  Crosshair,
  BookOpen,
  CreditCard,
  UserRound,
  Wallet,
  MessageSquare,
  DollarSign,
  TrendingUp,
  Image,
  Group,
  Gamepad2,
  HelpCircle,
  UsersRound,
  ChevronDown,
  ChevronUp,
  FileText
} from "lucide-react";

interface MenuLink {
  to: string;
  label: string;
  icon: React.ReactNode;
  isDropdown?: boolean;
  dropdownItems?: MenuLink[];
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

const iconMap = {
  calendar: <Calendar className="w-5 h-5" />,
  users: <Users className="w-5 h-5" />,
  clock: <Clock className="w-5 h-5" />,
  shopping: <ShoppingCart className="w-5 h-5" />,
  crosshair: <Crosshair className="w-5 h-5" />,
  book: <BookOpen className="w-5 h-5" />,
  credit: <CreditCard className="w-5 h-5" />,
  user: <UserRound className="w-5 h-5" />,
  wallet: <Wallet className="w-5 h-5" />,
  message: <MessageSquare className="w-5 h-5" />,
  dollar: <DollarSign className="w-5 h-5" />,
  trending: <TrendingUp className="w-5 h-5" />,
  image: <Image className="w-5 h-5" />,
  group: <Group className="w-5 h-5" />,
  gamepad: <Gamepad2 className="w-5 h-5" />,
  help: <HelpCircle className="w-5 h-5" />,
  communities: <UsersRound className="w-5 h-5" />,
  file: <FileText className="w-5 h-5" />
};

// Componente Portal Dropdown - Simplificado para mobile
const PortalDropdown: React.FC<{
  isOpen: boolean;
  position: DropdownPosition;
  items: MenuLink[];
  onClose: () => void;
  location: any;
}> = ({ isOpen, position, items, onClose, location }) => {
  if (!isOpen) return null;

  const dropdownContent = (
    <>
      {/* Overlay - apenas para fechar ao clicar fora */}
      <div 
        className="fixed inset-0 z-[999] bg-transparent" 
        onClick={(e) => {
          // Só fecha se clicar diretamente no overlay (não em elementos filhos)
          if (e.target === e.currentTarget) {
            e.stopPropagation();
            onClose();
          }
        }}
        onTouchStart={(e) => {
          // Só fecha se tocar diretamente no overlay, não em elementos filhos
          if (e.target === e.currentTarget) {
            e.stopPropagation();
            onClose();
          }
        }}
      />
      {/* Dropdown - sem animações para ser mais rápido */}
      <div 
        className="fixed z-[1000] bg-[#1A2233] border border-[#222A35] rounded-lg shadow-2xl min-w-[180px] max-w-[90vw]"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onClick={(e) => {
          // Previne que o clique dentro do dropdown feche o overlay
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // Previne que o touch dentro do dropdown feche o overlay
          e.stopPropagation();
        }}
      >
        {items.map((item, index) => {
          const isItemActive = 
            (item.to === location.pathname) || 
            (item.to !== "/painel-mentor" && item.to !== "/painel-admin" && item.to !== "/painel-jogador" && location.pathname.startsWith(item.to));
          
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => {
                // Fecha o dropdown imediatamente ao clicar
                onClose();
              }}
              className={`flex items-center gap-2 px-4 py-3 text-sm active:bg-[#223342] ${
                index === 0 ? 'rounded-t-lg' : ''
              } ${
                index === items.length - 1 ? 'rounded-b-lg' : ''
              } ${
                isItemActive
                  ? "bg-[#223342] text-[#00B6F0]"
                  : "text-gray-300"
              }`}
              style={{
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                position: 'relative',
                zIndex: 1001,
                display: 'block',
                width: '100%'
              }}
            >
              <span className={`${isItemActive ? "text-[#00B6F0]" : "text-gray-400"}`}>
                {item.icon}
              </span>
              {item.label}
            </Link>
          );
        })}
      </div>
    </>
  );

  return createPortal(dropdownContent, document.body);
};

export const TopFixedMenu: React.FC = () => {
  const { role } = useRole();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [expandedDropdown, setExpandedDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const getPanelLinks = (): MenuLink[] => {
    if (role === 'admin') {
      return [
        { to: "/painel-admin/jogos", label: "Jogos", icon: iconMap.gamepad },
        { to: "/painel-admin/usuarios", label: "Usuários", icon: iconMap.users },
        { to: "/painel-admin/mentorias", label: "Mentorias", icon: iconMap.crosshair },
        { to: "/painel-admin/banners", label: "Banners", icon: iconMap.image },
        { to: "/painel-admin/depoimentos", label: "Depoimentos", icon: iconMap.message },
        { to: "/painel-admin/hero", label: "Hero", icon: iconMap.image },
        { to: "/painel-admin/saques", label: "Saques", icon: iconMap.dollar },
        { to: "/painel-admin/financas", label: "Finanças", icon: iconMap.trending },
      ];
    }
    
    if (role === 'mentor') {
      return [
        { to: "/painel-mentor/disponibilidade", label: "Disponibilidade", icon: iconMap.calendar },
        { 
          to: "#", 
          label: "Mentorias", 
          icon: iconMap.users,
          isDropdown: true,
          dropdownItems: [
            { to: "/painel-mentor/minhas-mentorias", label: "Meus Serviços", icon: iconMap.users },
            { to: "/painel-mentor/sessoes-vendidas", label: "Vendidas", icon: iconMap.clock },
            { to: "/painel-mentor/sessoes-compradas", label: "Compradas", icon: iconMap.shopping },
            { to: "/painel-mentor/minhas-avaliacoes", label: "Avaliações", icon: iconMap.message },
          ]
        },

        { 
          to: "#", 
          label: "Comunidades", 
          icon: iconMap.group,
          isDropdown: true,
          dropdownItems: [
            { to: "/painel-mentor/comunidades", label: "Minhas Comunidades", icon: iconMap.group },
            { to: "/painel-mentor/comunidades-participo", label: "Participo", icon: iconMap.communities },
          ]
        },
        { to: "/painel-mentor/chat", label: "Chat", icon: iconMap.message },
        { to: "/painel-mentor/financas", label: "Finanças", icon: iconMap.wallet },
        { to: "/painel-mentor/perfil", label: "Perfil", icon: iconMap.user },
      ];
    }
    
    if (role === 'jogador') {
      return [
        { to: "/painel-jogador/mentorias-compradas", label: "Mentorias", icon: iconMap.book },
        { to: "/painel-jogador/minhas-comunidades", label: "Comunidades", icon: iconMap.communities },
        { to: "/painel-jogador/chat", label: "Chat", icon: iconMap.message },
        { to: "/painel-jogador/pagamentos", label: "Pagamentos", icon: iconMap.credit },
        { to: "/painel-jogador/perfil", label: "Perfil", icon: iconMap.user },
      ];
    }
    
    return [];
  };

  const links = getPanelLinks();

  // Check if any dropdown item is active
  const isDropdownActive = (dropdownItems?: MenuLink[]) => {
    if (!dropdownItems) return false;
    return dropdownItems.some(item => 
      location.pathname === item.to || 
      (item.to !== "/painel-mentor" && item.to !== "/painel-admin" && item.to !== "/painel-jogador" && location.pathname.startsWith(item.to))
    );
  };

  // Calculate dropdown position
  const calculateDropdownPosition = (buttonElement: HTMLButtonElement): DropdownPosition => {
    const rect = buttonElement.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    return {
      top: rect.bottom + scrollTop + 8, // 8px gap
      left: rect.left + scrollLeft + (rect.width / 2) - 90, // Center dropdown (180px / 2 = 90)
      width: rect.width
    };
  };

  // Toggle dropdown - Simplificado
  const toggleDropdown = (label: string, e?: React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Se já está aberto, fecha
    if (expandedDropdown === label) {
      setExpandedDropdown(null);
      return;
    }

    // Fecha qualquer dropdown aberto antes de abrir outro
    if (expandedDropdown) {
      setExpandedDropdown(null);
      // Pequeno delay para garantir que o dropdown anterior feche antes de abrir o novo
      setTimeout(() => {
        const buttonElement = buttonRefs.current[label];
        if (buttonElement) {
          const position = calculateDropdownPosition(buttonElement);
          setDropdownPosition(position);
          setExpandedDropdown(label);
        }
      }, 50);
    } else {
      // Abre diretamente se não há dropdown aberto
      const buttonElement = buttonRefs.current[label];
      if (buttonElement) {
        const position = calculateDropdownPosition(buttonElement);
        setDropdownPosition(position);
        setExpandedDropdown(label);
      }
    }
  };

  // Close dropdown
  const closeDropdown = () => {
    setExpandedDropdown(null);
  };

  // Auto scroll to active item on load - apenas quando a rota muda, não quando dropdown abre
  useEffect(() => {
    // Não fazer scroll se dropdown estiver aberto
    if (expandedDropdown) return;
    
    // Pequeno delay para garantir que o DOM esteja atualizado
    const timeoutId = setTimeout(() => {
      const activeIndex = links.findIndex(link => {
        if (link.isDropdown && link.dropdownItems) {
          return isDropdownActive(link.dropdownItems);
        }
        return (link.to === location.pathname) || 
               (link.to !== "/painel-admin" && link.to !== "/painel-mentor" && link.to !== "/painel-jogador" && location.pathname.startsWith(link.to));
      });
      
      if (activeIndex !== -1 && scrollRef.current) {
        const itemWidth = 120; // Approximate width of each item
        const containerWidth = scrollRef.current.offsetWidth;
        const scrollPosition = Math.max(0, (activeIndex * itemWidth) - (containerWidth / 2) + (itemWidth / 2));
        scrollRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, expandedDropdown]); // Adicionado expandedDropdown para não fazer scroll quando dropdown está aberto

  if (links.length === 0) return null;

  return (
    <>
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-[#181D24] to-[#1A2233] border-b border-[#222A35] shadow-lg">
        <div 
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-hide py-3 px-4 space-x-1 scroll-smooth"
          style={{ 
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth'
          }}
        >
          {links.map((link) => {
            if (link.isDropdown && link.dropdownItems) {
              const isActive = isDropdownActive(link.dropdownItems);
              const isExpanded = expandedDropdown === link.label;
              
              return (
                <div key={link.label} className="relative">
                  <button
                    ref={(el) => {
                      buttonRefs.current[link.label] = el;
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDropdown(link.label, e);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDropdown(link.label, e);
                    }}
                    className={`flex flex-col items-center justify-center min-w-[100px] py-2 px-3 rounded-lg whitespace-nowrap shrink-0 touch-manipulation ${
                      isActive
                        ? "bg-[#223342] text-[#00B6F0]"
                        : "text-gray-400"
                    }`}
                    style={{ 
                      scrollSnapAlign: 'center',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation'
                    }}
                  >
                    <span className={`mb-1 ${isActive ? "text-[#00B6F0]" : "text-gray-400"}`}>
                      {link.icon}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-medium text-center leading-tight">
                        {link.label}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                    {isActive && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-[#00B6F0] rounded-full"></div>
                    )}
                  </button>
                </div>
              );
            }
            
            // Regular link
            const isActive = 
              (link.to === location.pathname) || 
              (link.to !== "/painel-admin" && link.to !== "/painel-mentor" && link.to !== "/painel-jogador" && location.pathname.startsWith(link.to));
            
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex flex-col items-center justify-center min-w-[100px] py-2 px-3 rounded-lg whitespace-nowrap shrink-0 ${
                  isActive
                    ? "bg-[#223342] text-[#00B6F0]"
                    : "text-gray-400"
                }`}
                style={{ scrollSnapAlign: 'center' }}
              >
                <span className={`mb-1 ${isActive ? "text-[#00B6F0]" : "text-gray-400"}`}>
                  {link.icon}
                </span>
                <span className="text-xs font-medium text-center leading-tight">
                  {link.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-[#00B6F0] rounded-full"></div>
                )}
              </Link>
            );
          })}
        </div>
        
        {/* Progress indicator */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#222A35]">
          <div className="h-full bg-gradient-to-r from-[#00B6F0] to-[#5fceff] w-1/3 rounded-full transition-all duration-300"></div>
        </div>
      </div>

      {/* Portal Dropdown - Renderizado fora da hierarquia */}
      {expandedDropdown && links.find(l => l.label === expandedDropdown)?.dropdownItems && (
        <PortalDropdown
          isOpen={!!expandedDropdown}
          position={dropdownPosition}
          items={links.find(l => l.label === expandedDropdown)?.dropdownItems || []}
          onClose={closeDropdown}
          location={location}
        />
      )}
    </>
  );
}; 